/**
=========================================================
* Soft UI Dashboard PRO React - v3.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// @mui material components
import Card from "@mui/material/Card";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import Tooltip from "@mui/material/Tooltip";
import SuiBox from "components/SuiBox";
import Icon from "@mui/material/Icon";
import EditIcon from "@mui/icons-material/Edit";
import ClearIcon from "@mui/icons-material/Clear";
import DoneIcon from "@mui/icons-material/Done";
import IconButton from "@mui/material/IconButton";
import { Descriptions } from "antd";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import { moqOptions } from "utils/data";
import Divider from "@mui/material/Divider";

import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { useForm, Controller } from "react-hook-form";
import toast from "react-hot-toast";
import NumberFormat from "react-number-format";

const ProjectRequirement = ({ store, updateStore, editable = true }) => {
  const [editVisible, setEditVisible] = useState(false);

  const { register, handleSubmit, trigger, setValue, getValues, reset, errors, control } = useForm(
    {}
  );

  useEffect(() => {
    //set intial values
    let moqOpt = moqOptions.find((x) => x.value == store?.metadata?.moq);
    reset({
      moq: moqOpt,
      costs_price_range_min: store?.metadata?.costs_price_range_min,
      costs_price_range_max: store?.metadata?.costs_price_range_max,
      avg_sample_lead_time: store?.metadata?.avg_sample_lead_time,
      avg_production_lead_time: store?.metadata?.avg_production_lead_time,
    });
  }, [store]);

  const handleShowEdit = (e) => {
    setEditVisible(true);
  };

  const handleSave = async (data) => {
    if (data["costs_price_range_max"]) {
      data["costs_price_range_min"] = data["costs_price_range_min"] || 0;
    }
    if (data["costs_price_range_min"] > data["costs_price_range_max"]) {
      return toast.error("Costs price range is invalid!");
    }
    const payload = {
      metadata: {
        moq: data["moq"]?.value,
        costs_price_range_min: data["costs_price_range_min"],
        costs_price_range_max: data["costs_price_range_max"],
        avg_production_lead_time: data["avg_production_lead_time"],
        avg_sample_lead_time: data["avg_sample_lead_time"],
      },
    };
    try {
      await updateStore(payload);
      toast.success("Update project requirements successfully!");
      setEditVisible(false);
    } catch (err) {
      return toast.error("Update project requirements failed!");
    }
  };

  let avg_sample_lead_time = store?.metadata?.avg_sample_lead_time;
  if (avg_sample_lead_time) {
    avg_sample_lead_time = avg_sample_lead_time + " Day(s)";
  } else {
    avg_sample_lead_time = "-";
  }

  let avg_production_lead_time = store?.metadata?.avg_production_lead_time;
  if (avg_production_lead_time) {
    avg_production_lead_time = avg_production_lead_time + " Day(s)";
  } else {
    avg_production_lead_time = "-";
  }

  return (
    <Card sx={{ px: 2, minHeight: "100%" }}>
      <form onSubmit={handleSubmit(handleSave)}>
        <div className="m-4">
          <div className="my-2 flex justify-between">
            <SuiTypography variant="h6" fontWeight="medium" textTransform="capitalize">
              Project Requirements
            </SuiTypography>
            {editable &&
              (editVisible ? (
                <div className="flex">
                  <Tooltip title={"Cancel"} placement="top">
                    <IconButton
                      aria-label="clear"
                      size="small"
                      onClick={() => setEditVisible(false)}
                    >
                      <ClearIcon className="text-uss-red" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={"Save"} placement="top">
                    <IconButton aria-label="done" size="small" type="submit">
                      <DoneIcon color="success" />
                    </IconButton>
                  </Tooltip>
                </div>
              ) : (
                <Tooltip title={"Edit"} placement="top">
                  <IconButton aria-label="edit" size="small" onClick={handleShowEdit}>
                    <EditIcon color="dark" />
                    <SuiTypography
                      variant="h6"
                      fontWeight="bold"
                      textTransform="uppercase"
                      sx={{ ml: 1, fontSize: 14 }}
                      className="xsmall:hidden small:inline"
                    >
                      EDIT
                    </SuiTypography>
                  </IconButton>
                </Tooltip>
              ))}
          </div>

          <div>
            <SuiBox>
              <SuiTypography variant="button" fontWeight="bold" textTransform="capitalize">
                Minimum Order Quantity(MOQ):
              </SuiTypography>
              <div>
                {editVisible ? (
                  <SuiInput placeholder="MOQ" type="number" min={0} {...register("moq")} />
                ) : store?.metadata?.moq ? (
                  store.metadata.moq
                ) : (
                  "-"
                )}
              </div>
            </SuiBox>
            <SuiBox>
              <SuiTypography variant="button" fontWeight="bold" textTransform="capitalize">
                Manufacturing Costs Price Range:
              </SuiTypography>
              <div>
                {editVisible ? (
                  <div className="flex">
                    <SuiInput
                      icon={{ direction: "left", component: <AttachMoneyIcon /> }}
                      placeholder="Min"
                      type="number"
                      min={0}
                      {...register("costs_price_range_min")}
                    />
                    <Divider style={{ width: 20 }} />
                    <SuiInput
                      icon={{ direction: "left", component: <AttachMoneyIcon /> }}
                      placeholder="Max"
                      type="number"
                      min={0}
                      {...register("costs_price_range_max")}
                    />
                  </div>
                ) : (
                  <div className="flex justify-start">
                    <NumberFormat
                      value={store?.metadata?.costs_price_range_min}
                      displayType={"text"}
                      thousandSeparator={true}
                      prefix={"$"}
                      renderText={(value, props) => <div {...props}>{value}</div>}
                    />
                    -{" "}
                    <NumberFormat
                      value={store?.metadata?.costs_price_range_max}
                      displayType={"text"}
                      thousandSeparator={true}
                      prefix={"$"}
                      renderText={(value, props) => <div {...props}>{value}</div>}
                    />
                  </div>
                )}
              </div>
            </SuiBox>
            <SuiBox>
              <SuiTypography variant="button" fontWeight="bold" textTransform="capitalize">
                Average Sample Production Lead Time:
              </SuiTypography>
              <div>
                {editVisible ? (
                  <div className="flex items-center">
                    <SuiInput
                      placeholder="Sample Lead Time"
                      type="number"
                      min={0}
                      {...register("avg_sample_lead_time")}
                    />
                    <div className="flex whitespace-nowrap ml-1	">Day(s)</div>
                  </div>
                ) : (
                  avg_sample_lead_time
                )}
              </div>
            </SuiBox>
            <SuiBox>
              <SuiTypography variant="button" fontWeight="bold" textTransform="capitalize">
                Average Production Lead Time:
              </SuiTypography>
              <div>
                {editVisible ? (
                  <div className="flex items-center">
                    <SuiInput
                      placeholder="Production Lead Time"
                      type="number"
                      min={0}
                      {...register("avg_production_lead_time")}
                    />
                    <div className="flex whitespace-nowrap ml-1	">Day(s)</div>
                  </div>
                ) : (
                  avg_production_lead_time
                )}
              </div>
            </SuiBox>
          </div>
        </div>
      </form>
    </Card>
  );
};

export default ProjectRequirement;
